import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api
      .get(`/blog/${slug}`)
      .then(({ data }) => {
        setPost(data);
        setState("done");
      })
      .catch(() => setState("error"));
  }, [slug]);

  if (state === "loading")
    return <div className="flex h-screen items-center justify-center font-mono text-sm text-slate-500">Loading…</div>;

  if (state === "error" || !post)
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6" data-testid="post-not-found">
        <p className="font-display text-3xl text-white">Post not found</p>
        <Link to="/blog" className="link-underline font-mono text-sm uppercase tracking-[0.2em] text-brand-blue">
          Back to journal
        </Link>
      </div>
    );

  return (
    <article data-testid="blog-post-page" className="pt-32">
      <div className="mx-auto max-w-3xl px-6">
        <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-sm uppercase tracking-[0.2em] text-slate-400 hover:text-white">
          <ArrowLeft size={14} /> Journal
        </Link>

        <div className="mt-8 flex items-center gap-4 font-mono text-sm uppercase tracking-[0.2em]">
          <span className="text-brand-magenta">{post.category}</span>
          <span className="text-slate-500">{fmtDate(post.created_at)}</span>
          <span className="text-slate-500">· {post.author}</span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl"
        >
          {post.title}
        </motion.h1>
      </div>

      {post.cover_image && (
        <div className="mx-auto mt-12 max-w-4xl px-6">
          <div className="aspect-[16/9] overflow-hidden">
            <img src={post.cover_image} alt={post.title} className="h-full w-full object-cover" />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-6 py-16">
        {post.content.split("\n").filter(Boolean).map((para, i) => (
          <p key={i} className="mb-6 text-lg font-light leading-relaxed text-slate-300">
            {para}
          </p>
        ))}

        <div className="mt-16 border-t border-white/10 pt-10">
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 bg-brand-blue px-7 py-3.5 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta"
          >
            Train with Elevate Analytics →
          </Link>
        </div>
      </div>
    </article>
  );
}
